import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailLabelLanguage } from "../../../../../http/hrail/types/labelLanguages";
import { useHrailLabelLanguagesApi } from "../../../../../http/hrail/useLabelLanguages";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateLabelLanguageModalDialogButton from "./CreateLabelLanguageModalDialogButton";
import LabelLanguageForm from "./LabelLanguageForm";

type Props = {
  active?: boolean;
};

const LabelLanguages: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "labelLanguages" });
  const aborter = useRef(new AbortController());
  const labelLanguageApi = useHrailLabelLanguagesApi();
  const [itemToEdit, setItemToEdit] = useState<HrailLabelLanguage>();
  const [itemToDelete, setItemToDelete] = useState<HrailLabelLanguage>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "labelLanguages");
  const isActiveTab = active ?? true;
  const updateVisibility = async (labelLanguage: HrailLabelLanguage, visibility: VisibilityScopeValue) => {
    try {
      await labelLanguageApi.updateAdminHrailLabelLanguageVisibility(labelLanguage.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (labelLanguage: HrailLabelLanguage, newOrder: number) => {
    try {
      await labelLanguageApi.updateAdminHrailLabelLanguageOrder(labelLanguage.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<HrailLabelLanguage>("hrail.label_language");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = labelLanguageApi.useAdminHrailLabelLanguages(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.labelLanguages.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.labelLanguages.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateLabelLanguageModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailLabelLanguage>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.name}</th>
            <th align="left">{i18n.translation.common.extraItem}</th>
            <th align="right">{i18n.translation.common.factor}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(labelLanguage) => (
          <>
            <Table.Td noWrap={true}>{labelLanguage.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{labelLanguage.name ?? ""}</Table.Td>
            <Table.Td noWrap={true}>{labelLanguage.extraItem?.code ?? ""}</Table.Td>
            <Table.Td align="right" noWrap={false}>
              {formatNullableNumber(labelLanguage.factorPerLabel, "")}
            </Table.Td>
            <td align="center">
              <Visibility
                value={labelLanguage.visibility}
                onChange={(value) => updateVisibility(labelLanguage, value)}
              />
            </td>
            <td align="center">
              <InlineOrderEditor
                order={labelLanguage.order}
                onUpdate={(newOrder) => updateOrder(labelLanguage, newOrder)}
              />
            </td>
          </>
        )}
        onRowClick={(labelLanguage) => setItemToEdit(labelLanguage)}
        rowActions={(labelLanguage) => [
          getEditAction(i18n, () => setItemToEdit(labelLanguage)),
          accessesAction(labelLanguage),
          getDeleteAction(i18n, () => setItemToDelete(labelLanguage), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.labelLanguages.edit}>
          <LabelLanguageForm labelLanguage={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.labelLanguages.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                labelLanguageApi
                  .deleteAdminHrailLabelLanguage(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.labelLanguages.deleteLabel,
            [itemToDelete.code ?? "", itemToDelete.name ?? ""].filter(Boolean).join(" - ") || itemToDelete.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

const formatNullableNumber = (value: number | null | undefined, suffix = "") =>
  typeof value === "number" ? String(value) + suffix : "";

export default LabelLanguages;
